import React, { useState, useEffect, useRef } from "react"
import html2canvas from "html2canvas"
import { Chart } from "chart.js"
import { LoadingOutlined } from "@ant-design/icons"
import ImplementationTrendChart from "./ImplementationRubric/ImplementationTrendChart"
import ProgramMatrixSELQuality from "./ProgramMatrix/SELQuality"
import pdfSvg from "../../assets/images/pdf-file.svg"

// ProgramMatrix/SELQuality.jsx's own charts live at these element ids (see its pdfContent1/2/3/9
// divs) — the same ids the page's own "Program Matrix" panel already renders on-screen, so these
// lookups must be scoped (via a ref, below) to this component's own off-screen copy rather than
// using a page-wide document.getElementById, which would ambiguously match whichever copy
// happens to come first in the DOM. pdfContent5/6/7/8 belong to SELSchoolCapacity, which
// SELQuality.jsx nests inside itself (inside its own pdfContent4 wrapper) whenever its internal
// type state is "student" — the same condition pdfContent9 already relies on.
const SEL_QUALITY_ELEMENT_IDS = {
  grade_level: "pdfContent9",
  universal_support: "pdfContent1",
  data_tier: "pdfContent2",
  competency_dessa: "pdfContent3",
  students_sel_indicator: "pdfContent5",
  students_matrix_competency: "pdfContent6",
  students_dessa_competency: "pdfContent7",
  capacity_vs_actual: "pdfContent8",
}

const csrfToken = () => document.querySelector("[name=csrf-token]").content

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

const captureChart = (element) => (element
  ? html2canvas(element, { scale: 2 }).then((canvas) => canvas.toDataURL("image/png"))
  : Promise.resolve(null))

// How often to check whether the off-screen charts are ready, how many unchanged checks in a row
// count as "settled", and the longest to wait before capturing whatever is there.
const READY_POLL_MS = 250
const READY_STABLE_CHECKS = 3
const READY_MIN_MS = 800
const READY_MAX_MS = 20000

// SELQuality.jsx loads its data itself (APIClient -> window.fetch, two chained requests) after it
// mounts, so the off-screen copy isn't ready until those finish. This counts the requests started
// while it's being rendered, so the capture can wait for them without changing SELQuality.jsx.
// It must be installed before that copy mounts — child effects (its fetch) run before ours.
const trackFetches = () => {
  const originalFetch = window.fetch
  const tracker = { pending: 0 }
  window.fetch = (...args) => {
    tracker.pending += 1
    return originalFetch(...args).finally(() => {
      tracker.pending -= 1
    })
  }
  tracker.restore = () => {
    window.fetch = originalFetch
  }
  return tracker
}

// A snapshot of every chart's data under the given elements — once it stops changing, React has
// applied the loaded data to every chart.
const chartsSignature = (elements) => JSON.stringify(
  elements.filter(Boolean).flatMap((element) => Array.from(element.querySelectorAll("canvas")).map((canvas) => {
    const chart = Chart.getChart(canvas)
    return chart ? chart.data.datasets.map((dataset) => dataset.data) : null
  })),
)

// section: null downloads the whole combined report; any other value (see
// SchoolProgressReportsController::SECTION_TITLES) downloads just that one part, in the same
// layout it has inside the combined PDF — and only the charts that part needs are rendered
// off-screen and captured.
const SchoolProgressReportButton = ({
  href,
  implementation,
  programMatrix,
  mtssTypes,
  selQualityToggle,
  section = null,
  fileName = "School Progress Report.pdf",
}) => {
  const [status, setStatus] = useState("idle")
  const implementationChartRef = useRef(null)
  const selQualityRef = useRef(null)
  const renderSessionRef = useRef(null)
  const hasImplementationChart = Boolean(
    implementation && implementation.present
    && (!section || section === "implementation"),
  )
  const hasSelQuality = Boolean(
    programMatrix && programMatrix.present
    && (!section || section === "sel_quality"),
  )
  // The Student/Adult report the off-screen SEL Quality copy actually renders: SELQuality.jsx
  // always opens on "student" and only switches itself to "adult" when the student side has no
  // data, and the PDF has to describe the charts that were really captured.
  const qualityReport = (hasSelQuality && programMatrix.quality_report) || {}
  const studentHasData = Boolean(
    qualityReport.student && qualityReport.student.items.length > 0,
  )
  const adultHasData = Boolean(
    qualityReport.adult && qualityReport.adult.items.length > 0,
  )
  // The on-screen SEL Quality toggle when the page reports it, else the same default that
  // SELQuality.jsx itself would open on.
  const selQualityType = selQualityToggle
    || (!studentHasData && adultHasData ? "adult" : "student")

  // ProgramMatrix/SELQuality.jsx (and SELSchoolCapacity.jsx, which it renders for the student
  // report) read these straight off window.gon rather than taking them as props — the same
  // globals ProgramMatrixDetail (SchoolProgressReport.jsx) sets for the on-screen copy of this
  // component. That doesn't help here: antd's Collapse only mounts a Panel's children on first
  // expand, so if the user downloads the report without ever opening the Program Matrix panel,
  // ProgramMatrixDetail never ran and never set them — crashing this component's own off-screen
  // copy on makeDessaArray(window.gon.SEL_STANDARDS).forEach. Set synchronously during render
  // (not in an effect) so they're in place before this component's own mount-time effects read
  // them, same reasoning as ProgramMatrixDetail's own version of this assignment.
  if (hasSelQuality) {
    window.gon = window.gon || {}
    window.gon.CURRENT_SCHOOL_ACTIVITIES = programMatrix.current_school_activities || []
    window.gon.SEL_STANDARDS = programMatrix.sel_standards || []
    window.gon.DESS_SEL_STANDARDS = programMatrix.dessa_standards || []
  }

  const submitReport = (implementationChartImage, selQualityChartImages) => {
    fetch(href, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        implementation_chart_image: implementationChartImage,
        sel_quality_chart_images: selQualityChartImages,
        sel_quality_type: selQualityType,
        mtss_indicator_types: mtssTypes || [],
        section,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`)
        }
        return response.blob()
      })
      .then((blob) => downloadBlob(blob, fileName))
      .catch((error) => {
        console.error("Failed to download school progress report", error)
      })
      .finally(() => {
        setStatus("idle")
      })
  }

  // Undoes handleClick's setup: the fetch counter and the paused Chart.js animations.
  const endRenderSession = () => {
    const session = renderSessionRef.current
    if (!session) {
      return
    }
    session.tracker.restore()
    Chart.defaults.animation = session.animation
    renderSessionRef.current = null
  }

  const handleClick = () => {
    // Chart.js animates bars up from zero, so a capture mid-animation shows empty or short bars.
    // Animations stay off only while the off-screen copies render, then go back to normal.
    renderSessionRef.current = {
      tracker: trackFetches(),
      animation: Chart.defaults.animation,
    }
    Chart.defaults.animation = false
    setStatus("rendering")
  }

  useEffect(() => endRenderSession, [])

  // Captures each of ProgramMatrix/SELQuality.jsx's own pdfContent1/2/3/5-9 divs, scoped to this
  // component's own off-screen copy (see SEL_QUALITY_ELEMENT_IDS above) — any that aren't found,
  // e.g. pdfContent9 when the adult/student toggle inside that copy isn't on "student", are left
  // out and ProgramMatrixReportPdf falls back to its native chart for just that one.
  const captureSelQualityCharts = () => {
    if (!hasSelQuality || !selQualityRef.current) {
      return Promise.resolve({})
    }

    const entries = Object.entries(SEL_QUALITY_ELEMENT_IDS)
    return Promise.all(
      entries.map(([key, elementId]) => captureChart(selQualityRef.current.querySelector(`#${elementId}`)).then(
        (image) => [key, image],
      )),
    ).then((results) => Object.fromEntries(results.filter(([, image]) => image)))
  }

  useEffect(() => {
    if (status !== "rendering") {
      return undefined
    }

    if (!hasImplementationChart && !hasSelQuality) {
      endRenderSession()
      submitReport(null, {})
      return undefined
    }

    // Waits until the off-screen charts are fully drawn: no data requests still in flight and
    // every chart's data unchanged for a few checks in a row (falling back to capturing anyway
    // after READY_MAX_MS, so a stuck request can't hang the download).
    const startedAt = Date.now()
    let lastSignature = null
    let stableChecks = 0
    let timeoutId = null

    const capture = () => {
      Promise.all([
        captureChart(
          hasImplementationChart ? implementationChartRef.current : null,
        ),
        captureSelQualityCharts(),
      ]).then(([implementationChartImage, selQualityChartImages]) => {
        endRenderSession()
        submitReport(implementationChartImage, selQualityChartImages)
      })
    }

    const checkReady = () => {
      const session = renderSessionRef.current
      const elapsed = Date.now() - startedAt
      const signature = chartsSignature([implementationChartRef.current, selQualityRef.current])
      const idle = !session || session.tracker.pending === 0

      stableChecks = idle && signature === lastSignature ? stableChecks + 1 : 0
      lastSignature = signature

      const settled = elapsed >= READY_MIN_MS && stableChecks >= READY_STABLE_CHECKS
      if (settled || elapsed >= READY_MAX_MS) {
        capture()
      } else {
        timeoutId = setTimeout(checkReady, READY_POLL_MS)
      }
    }

    timeoutId = setTimeout(checkReady, READY_POLL_MS)

    return () => clearTimeout(timeoutId)
  }, [status])

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={status !== "idle"}
        className="open-modal-button spr-download-btn"
        aria-label={status === "idle" ? "Download PDF" : "Generating PDF"}
        title={status === "idle" ? "Download PDF" : "Generating PDF…"}
      >
        {status === "idle" ? (
          <img src={pdfSvg} alt="Download PDF" className="pdf-icon-button" />
        ) : (
          <LoadingOutlined className="spr-download-btn__spinner" />
        )}
      </button>
      {status === "rendering" && (
        <div
          style={{
            position: "fixed",
            top: "-9999px",
            left: "-9999px",
            width: "500px",
          }}
        >
          {hasImplementationChart && (
            <div
              ref={implementationChartRef}
              style={{
                position: "relative",
                width: "1100px",
                height: "520px",
                background: "#fff",
              }}
            >
              <ImplementationTrendChart
                rubric={implementation}
                fixedSize={{ width: 1100, height: 520 }}
              />
            </div>
          )}
          {hasSelQuality && (
            // Wider than the 500px outer container — ProgramMatrix/SELQuality.jsx's own charts
            // are built for a desktop panel's width, not this off-screen strip's narrower default.
            <div
              ref={selQualityRef}
              style={{
                position: "relative",
                width: "900px",
                background: "#fff",
              }}
            >
              <ProgramMatrixSELQuality
                matrix={programMatrix}
                currentSchool={null}
                initialType={selQualityType}
              />
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default SchoolProgressReportButton
